'use client';

import Image from '@/components/Image';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { setSession } from '@/utils/tools';
import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

// BlueLightBanner 组件接口定义
interface BlueLightBannerProps {
  bannerData: {
    title?: string;
    description?: string;
    indoor_image?: string;
    outdoor_image?: string;
    indoor_words?: string;
    outdoor_words?: string;
    aspectRatio?: string;
    group?: string;
  };
  onBannerExp?: (params: { group: string }) => void;
  onBannerClick?: (params: { group: string }) => void;
}

export default function BlueLightBanner({
  bannerData,
  onBannerExp,
  onBannerClick,
}: BlueLightBannerProps) {
  const [activeType, setActiveType] = useState<'indoor' | 'outdoor'>('indoor');
  const [isMobile, setIsMobile] = useState(false);
  const { navigate } = useAppNavigation();

  // 使用 useInView 进行版位曝光检测
  const [expRef, inView] = useInView({
    threshold: 0.5, // 50%可见时触发
    triggerOnce: true, // 只触发一次
  });

  // 检测设备类型
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Banner曝光埋点处理
  useEffect(() => {
    if (inView && onBannerExp) {
      onBannerExp({
        group: bannerData.group,
      });
    }
  }, [inView, onBannerExp, bannerData.group]);

  // Banner点击跳转和埋点
  const handleBannerClick = useCallback(() => {
    if (bannerData.group) {
      // 触发点击埋点
      if (onBannerClick) {
        onBannerClick({
          group: bannerData.group,
        });
      }
      // 使用 navigate 进行页面跳转，支持app内嵌环境
      setSession('zl_goodsListEnterPageScene', '活动专题页');
      navigate(`/goods-list/${bannerData.group}`);
    }
  }, [bannerData.group, onBannerClick, navigate]);

  if (!bannerData) return null;

  const aspectRatio = bannerData.aspectRatio || '1148/860';

  return (
    <div
      ref={expRef}
      className="blue-banner flex flex-col lg:flex-row items-center justify-center lg:justify-normal"
    >
      {/* 图片区域 */}
      <div
        className="img-wrap-box w-full lg:w-auto"
        style={{ aspectRatio: isMobile ? '375/470' : aspectRatio }}
      >
        {bannerData.group ? (
          <a
            href={`/goods-list/${bannerData.group}`}
            aria-label={bannerData.title}
            onClick={(e) => {
              e.preventDefault();
              handleBannerClick();
            }}
          >
            <div
              className="img-wrap relative"
              style={{ aspectRatio: isMobile ? '375/470' : aspectRatio }}
            >
              <Image
                className={`block w-full lg:w-[59vw] transition-opacity duration-500 ease-in-out ${
                  activeType === 'indoor' ? 'opacity-100' : 'opacity-0'
                }`}
                src={bannerData.indoor_image || ''}
                alt={bannerData.title}
                width={1148}
                height={860}
              />
              <Image
                className={`out-img absolute top-0 left-0 block w-full lg:w-[59vw] transition-opacity duration-500 ease-in-out ${
                  activeType === 'outdoor' ? 'opacity-100' : 'opacity-0'
                }`}
                src={bannerData.outdoor_image || ''}
                alt={bannerData.title}
                width={1148}
                height={860}
              />
            </div>
          </a>
        ) : (
          <div
            className="img-wrap relative"
            style={{ aspectRatio: isMobile ? '375/470' : aspectRatio }}
          >
            <Image
              className={`block w-full lg:w-[59vw] transition-opacity duration-500 ease-in-out ${
                activeType === 'indoor' ? 'opacity-100' : 'opacity-0'
              }`}
              src={bannerData.indoor_image || ''}
              alt={bannerData.title}
              width={1148}
              height={860}
            />
            <Image
              className={`out-img absolute top-0 left-0 block w-full lg:w-[59vw] transition-opacity duration-500 ease-in-out ${
                activeType === 'outdoor' ? 'opacity-100' : 'opacity-0'
              }`}
              src={bannerData.outdoor_image || ''}
              alt={bannerData.title}
              width={1148}
              height={860}
            />
          </div>
        )}
      </div>

      {/* 右侧内容区域 */}
      <div className="right-part px-[24px] lg:px-0 lg:ml-5 pb-11 lg:pb-0 relative">
        <h1
          className="title mb-[4px] lg:mb-[1.04vw] text-[32px] lg:text-[3.33vw] leading-[37px] lg:leading-[3.7vw] font-bs text-left"
          style={{
            background: 'linear-gradient(90deg, #FFFFFF 0%, #A1BCFF 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            position: isMobile ? 'absolute' : 'static',
            bottom: isMobile ? '150px' : 'auto',
          }}
          dangerouslySetInnerHTML={{ __html: bannerData.title || '' }}
        />
        <h3 className="description text-[12px] xl:text-[0.83vw] leading-[21px] text-[#dedee1]">
          {bannerData.description}
        </h3>
        <div className="bottom-btn border border-[#e5e5e5] rounded-[36px] w-fit mt-[16px] lg:mt-[2.5vw]">
          <p className="flex items-center p-[5px] lg:p-2 w-fit">
            <span
              className={`left-word block flex items-center justify-center w-[82px] lg:w-[120px] h-[30px] lg:h-[48px] text-[11px] lg:text-[14px] text-[#dedee1] rounded-[36px] cursor-pointer transition-transform duration-200 group ${
                activeType === 'indoor' ? 'bg-white' : ''
              }`}
              onClick={() => setActiveType('indoor')}
            >
              <span
                className={`word transition-transform duration-200 group-hover:scale-110 ${activeType === 'indoor' ? 'font-bs text-[#01000e]' : ''}`}
              >
                {bannerData.indoor_words}
              </span>
            </span>
            <span
              className={`right-word block flex items-center justify-center w-[82px] lg:w-[120px] h-[30px] lg:h-[48px] text-[11px] lg:text-[14px] text-[#dedee1] rounded-[36px] cursor-pointer transition-transform duration-200 group ${
                activeType === 'outdoor' ? 'bg-white' : ''
              }`}
              onClick={() => setActiveType('outdoor')}
            >
              <span
                className={`word transition-transform duration-200 group-hover:scale-110 ${activeType === 'outdoor' ? 'font-bs text-[#01000e]' : ''}`}
              >
                {bannerData.outdoor_words}
              </span>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
